import { useTranslation, Trans } from 'react-i18next';
import React, { useState, useMemo, useCallback } from 'react';
import cx from 'clsx';
import dayjs from 'dayjs';

import {
  IconRcStreamDrawHot,
  IconRcStreamPollTick,
  IconRcStreamPollLoading,
} from '@/assets/stream';
import { HelperTooltip, OrgVerifiedLogo, RestrictionTip } from '@/components';
import { MobileModalTip } from '@/components/shared/MobileModalTip';
import { formatNum, isOfficalAddress, login } from '@/utils';
import {
  useSwitch,
  useCurrentAddressInfo,
  useDeviceTypeIsMobile,
  useTheme,
  OfficialAccountVerifyStatus,
} from '@/hooks';
import { FeedArticle, useFeedVote, StreamUsersModal, useFeedVoters } from '@/views/Stream';

import style from './index.module.scss';

type PollCardProps = {
  article: FeedArticle;
  isDetails?: boolean;
};

export const PollCard = ({ article }: PollCardProps) => {
  const { t } = useTranslation();

  const isMobile = useDeviceTypeIsMobile();
  const { isDark } = useTheme();
  const poll = useMemo(() => article.poll!, [article.poll]);
  const { isVerified, address } = useCurrentAddressInfo();
  const [voterChoiceIndex, setVoterChoiceIndex] = useState<string>();

  const [selectedIdx, setSelectedIdx] = useState<number>();
  const [voting, setVoting] = useState(false);
  const { on: mobileTip, turnOn: showMobileTip, turn: setMobileTip } = useSwitch();
  const { on: isUserList, turnOn: showUserList, turnOff: hideUserList } = useSwitch();

  const isOfficialCreator = useMemo(
    () => isOfficalAddress(article.creator_id),
    [article.creator_id],
  );

  const leftTime = useMemo(() => {
    const left = poll.create_at + poll.duration - Date.now() / 1000;
    if (left > 0) {
      return t('PollCard.timeLeft', { time: dayjs.duration(left, 'seconds').humanize() });
    }
  }, [poll, t]);

  const isSelf = useMemo(() => {
    return address === article.creator_id;
  }, [address, article.creator_id]);

  const hasPermission = useMemo(
    () => article?.poll?.has_permission,
    [article?.poll?.has_permission],
  );

  const isVoteable = useMemo(() => {
    return !!leftTime && !isSelf && !poll.is_voted && hasPermission;
  }, [hasPermission, isSelf, leftTime, poll.is_voted]);

  const { mutateAsync: vote } = useFeedVote(article.id, poll.id);

  const handleOptionClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      if (isMobile && leftTime) {
        showMobileTip();
        e.stopPropagation();
        return;
      }

      if (!isVerified && isVoteable) {
        login();
        e.stopPropagation();
        return;
      }

      if (!isVoteable) {
        return;
      }

      e.stopPropagation();
      const { idx } = e.currentTarget.dataset;

      setSelectedIdx(Number(idx));
    },
    [isVoteable, isMobile, showMobileTip, isVerified, leftTime],
  );

  const handleVoteConfirm = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      try {
        setVoting(true);
        if (typeof selectedIdx !== 'undefined') {
          await vote(selectedIdx);
        }
      } finally {
        setSelectedIdx(undefined);
        setVoting(false);
      }
    },
    [selectedIdx, vote],
  );

  const handleCancelVoteClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    setSelectedIdx(undefined);
  }, []);

  const handleUserListClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();

      if (isMobile) {
        showMobileTip();
        return;
      }

      showUserList();
      setVoterChoiceIndex(undefined);
    },
    [showUserList, isMobile, showMobileTip],
  );

  const handleVoteCountClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      if (isMobile) {
        showMobileTip();
        return;
      }

      showUserList();
      setVoterChoiceIndex(e.currentTarget.dataset.idx);
    },
    [showUserList, isMobile, showMobileTip],
  );

  const voterModalTitle = useMemo(() => {
    return typeof voterChoiceIndex === 'undefined'
      ? t('PollCard.votes')
      : `${t('Choice.choice')} ${Number(voterChoiceIndex) + 1}`;
  }, [voterChoiceIndex, t]);

  return (
    <div className={cx(isMobile && style.mobile)}>
      <div className={cx(style.pollCard, isDark && style.isDark)}>
        <div className={style.title}>
          <div className={cx(style.creator, isOfficialCreator && style.isOfficial)}>
            <Trans i18nKey="PollCard.createdByOfficial" values={{ name: article._author }}>
              Poll created by{' '}
              {isOfficialCreator ? (
                <OrgVerifiedLogo
                  size={16}
                  className={style.officialIcon}
                  marginLeft={0}
                  marginRight={4}
                  disableTooltip
                  unverified={
                    (article?.creator as OfficialAccount)?.verify_status !==
                    OfficialAccountVerifyStatus.VERIFIED
                  }
                />
              ) : null}{' '}
              {article._author}
            </Trans>
          </div>
          <div className={cx(style.cardStatus, leftTime && style.isGoing)}>
            {leftTime ? t('Choice.in-progress') : t('Choice.completed')}
          </div>
          {leftTime ? <div className={style.leftTime}>{leftTime}</div> : null}
        </div>
        <div className={style.body}>
          {poll.options.map((x, i) => (
            <div className={style.optionWrap} key={x + i}>
              <HelperTooltip
                title={hasPermission ? undefined : t('PollCard.noPermission')}
                align={{ targetOffset: [0, -10] }}>
                <div
                  className={cx(
                    style.option,
                    isVoteable && style.isSelectable,
                    poll.is_voted && poll.voted_option_index === i && style.isVoted,
                    !hasPermission && style.noPermission,
                  )}
                  data-idx={i}
                  onClick={handleOptionClick}>
                  {(!isVoteable || isSelf) && poll._voteStats?.[i]?.percent ? (
                    <div
                      className={style.progress}
                      style={{ width: `${poll._voteStats?.[i]?.percent}%` }}
                    />
                  ) : null}
                  <div className={style.optionIndex}>{i + 1}</div>
                  <div className={style.optionContent}>
                    <span className={style.optionContentText}>{x}</span>
                    {poll.is_voted && poll.voted_option_index === i ? (
                      <IconRcStreamPollTick className={style.optionTickIcon} />
                    ) : null}
                  </div>
                  {!isVoteable ? (
                    <div
                      className={cx(
                        style.optionStats,
                        poll._voteStats?.[i]?.count && style.isClickable,
                      )}
                      onClick={poll._voteStats?.[i]?.count ? handleVoteCountClick : undefined}
                      data-idx={i}>
                      <span className={style.optionFirst}>
                        {poll._voteStats?.[i]?.formatedPercent || '0%'}
                      </span>
                      <span className={style.optionSecond}>{poll._voteStats?.[i]?.count || 0}</span>
                    </div>
                  ) : null}
                  {isVoteable && selectedIdx === i ? (
                    <div className={cx(style.voteMask, voting && style.isVoting)}>
                      {voting ? (
                        <IconRcStreamPollLoading className={style.votingIcon} />
                      ) : (
                        <>
                          <div className={style.voteConfirm} onClick={handleVoteConfirm}>
                            {t('PollCard.1udtsrv')}
                          </div>
                          <div className={style.voteCancel} onClick={handleCancelVoteClick}>
                            {t('PollCard.x9d2fu')}
                          </div>
                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              </HelperTooltip>
            </div>
          ))}
        </div>
        <div className={style.footer}>
          <RestrictionTip data={poll?.permissions} />
          <div className={style.footerRight}>
            <IconRcStreamDrawHot className={style.hotIcon} />
            <span
              className={cx(style.userCount, poll.vote_count && style.isClickable)}
              onClick={poll.vote_count ? handleUserListClick : undefined}>
              {formatNum(poll.vote_count)} {t('PollCard.1ux22x')}
            </span>
          </div>
        </div>
        {isMobile && mobileTip ? (
          <MobileModalTip visible={mobileTip} setVisible={setMobileTip} />
        ) : null}
        {isUserList ? (
          <StreamUsersModal
            on={isUserList}
            title={voterModalTitle}
            turnOff={hideUserList}
            hook={useFeedVoters}
            params={[poll.id, voterChoiceIndex]}
          />
        ) : null}
      </div>
    </div>
  );
};
